import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutHeader as Header, LayoutFooter as Footer, LayoutHero as Hero } from '@/layouts';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Search, SlidersHorizontal, MessageCircle } from 'lucide-react';
import { useAppData, useFilteredVehicles } from '@/contexts/AppDataContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { Layout6Root } from './Layout6Root';
import { HomeShowcase } from './HomeShowcase';
import './styles.css';
import './home.css';

const Index = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const contextVehicles = useFilteredVehicles(selectedStore);
  const [vehicles, setVehicles] = useState(contextVehicles);
  const [filteredVehicles, setFilteredVehicles] = useState(contextVehicles);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedFuel, setSelectedFuel] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [showFilters, setShowFilters] = useState(false);
  const store = selectedStore || config.stores[0];

  useEffect(() => {
    setVehicles(contextVehicles);
    setFilteredVehicles(contextVehicles);
  }, [contextVehicles]);

  const brands = Array.from(new Set(vehicles.map((v) => v.brand))).sort();
  const fuels = Array.from(new Set(vehicles.map((v) => v.fuel))).sort();

  const suggestions = useMemo(() => {
    if (searchTerm.length < 2) return [];
    const term = searchTerm.toLowerCase();
    const results: Array<{ type: string; text: string; vehicle?: any }> = [];

    brands.forEach((brand) => {
      if (brand.toLowerCase().includes(term)) {
        results.push({ type: 'brand', text: brand });
      }
    });

    const models = Array.from(new Set(vehicles.map((v) => `${v.brand} ${v.model}`)));
    models.forEach((model) => {
      if (model.toLowerCase().includes(term) && !results.find((s) => s.text === model)) {
        results.push({ type: 'model', text: model });
      }
    });

    vehicles.forEach((vehicle) => {
      const fullText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase();
      if (fullText.includes(term) && results.length < 10) {
        const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`;
        if (!results.find((s) => s.text === vehicleText)) {
          results.push({ type: 'vehicle', text: vehicleText, vehicle });
        }
      }
    });

    return results.slice(0, 8);
  }, [searchTerm, vehicles, brands]);

  useEffect(() => {
    const filtered = vehicles.filter((vehicle) => {
      const matchesSearch =
        searchTerm === '' ||
        `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBrand = selectedBrand === 'all' || vehicle.brand === selectedBrand;
      const matchesFuel = selectedFuel === 'all' || vehicle.fuel === selectedFuel;
      const matchesType =
        selectedType === 'all' ||
        (selectedType === 'zerokm' && (vehicle.isZeroKm || vehicle.mileage < 100)) ||
        (selectedType === 'seminovo' && !vehicle.isZeroKm && vehicle.mileage >= 100);
      const matchesPrice = vehicle.price >= priceRange[0] && vehicle.price <= priceRange[1];
      return matchesSearch && matchesBrand && matchesFuel && matchesType && matchesPrice;
    });
    setFilteredVehicles(filtered);
  }, [vehicles, searchTerm, selectedBrand, selectedFuel, selectedType, priceRange]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBrand('all');
    setSelectedFuel('all');
    setSelectedType('all');
    setPriceRange([0, 1000000]);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: any) => {
    if (suggestion.type === 'vehicle' && suggestion.vehicle) {
      const slug = generateVehicleSlug(suggestion.vehicle);
      navigate(`/estoque/${slug}`);
    } else {
      setSearchTerm(suggestion.text);
      setShowSuggestions(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setShowSuggestions(value.length >= 2);
    setSelectedSuggestionIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Layout6Root withHeader className="wl6-home-page">
      <Header config={config} />
      <Hero config={config} />

      <HomeShowcase vehicles={vehicles} config={config} store={store} />

      {/* Hidden section — logic preserved */}
      <section id="estoque-old" className="py-16 bg-background" style={{ display: 'none' }}>
        <div className="container mx-auto px-4">
          <Card
            className={`mb-8 shadow-card border-2 transition-all duration-200 ${
              isSearchFocused ? 'border-primary ring-2 ring-primary/20' : 'border-border'
            }`}
          >
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4 mb-4">
                <div className="flex-1 relative" ref={searchRef}>
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                  <Input
                    placeholder="Digite marca, modelo, ano ou qualquer palavra-chave..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                      setIsSearchFocused(true);
                      searchTerm.length >= 2 && setShowSuggestions(true);
                    }}
                    onBlur={() => setIsSearchFocused(false)}
                    className="pl-10 h-12 text-lg focus-visible:ring-0 focus-visible:ring-offset-0 border-0"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-[9999] max-h-[400px] overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 ${
                            index === selectedSuggestionIndex ? 'bg-muted/50' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {suggestion.type === 'vehicle' && suggestion.vehicle && (
                              <img
                                src={suggestion.vehicle.photos[0]}
                                alt={suggestion.text}
                                className="w-12 h-12 object-cover rounded"
                              />
                            )}
                            <span className="font-medium text-sm truncate">{suggestion.text}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 h-12 px-6"
                >
                  <SlidersHorizontal className="h-5 w-5" />
                  Filtros Avançados
                </Button>
              </div>

              {showFilters && (
                <div className="border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                      <SelectTrigger>
                        <SelectValue placeholder="Marca" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as Marcas</SelectItem>
                        {brands.map((brand) => (
                          <SelectItem key={brand} value={brand}>
                            {brand}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedFuel} onValueChange={setSelectedFuel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Combustível" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {fuels.map((fuel) => (
                          <SelectItem key={fuel} value={fuel}>
                            {fuel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedType} onValueChange={setSelectedType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {store?.ad?.showZeroKm && <SelectItem value="zerokm">Zero KM</SelectItem>}
                        {store?.ad?.showUsed && <SelectItem value="seminovo">Seminovos</SelectItem>}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={clearFilters}>
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {filteredVehicles.length} veículo{filteredVehicles.length !== 1 ? 's' : ''} encontrado
                  {filteredVehicles.length !== 1 ? 's' : ''}
                </span>
              </div>
            </CardContent>
          </Card>

          {filteredVehicles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredVehicles.slice(0, 12).map((vehicle) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} whatsappUrl={store.whatsapp?.ios} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Button onClick={clearFilters}>Limpar Filtros</Button>
              <Button variant="outline" onClick={() => openWhatsApp(store.whatsapp?.ios)} className="ml-2">
                <MessageCircle className="h-4 w-4 mr-2" />
                Falar com Vendedor
              </Button>
            </div>
          )}
        </div>
      </section>

      <Footer config={config} />
    </Layout6Root>
  );
};

export default Index;
